import React, { useState, useEffect } from 'react';
import type { Expense, ExpenseCategory } from '../types/expense';
import { createExpense, updateExpense } from '../services/expenseService';
import { X, DollarSign, AlignLeft, Store } from 'lucide-react';

interface ExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  editingExpense?: Expense | null;
  defaultDate?: string;
}

export const CATEGORY_CONFIG: Record<
  ExpenseCategory,
  { label: string; emoji: string; color: string; text: string }
> = {
  food:          { label: 'Food & Drink',   emoji: '🍔', color: 'bg-orange-500/15',  text: 'text-orange-400' },
  transport:     { label: 'Transport',       emoji: '🚗', color: 'bg-blue-500/15',    text: 'text-blue-400' },
  shopping:      { label: 'Shopping',        emoji: '🛍️', color: 'bg-pink-500/15',    text: 'text-pink-400' },
  entertainment: { label: 'Entertainment',   emoji: '🎬', color: 'bg-purple-500/15',  text: 'text-purple-400' },
  health:        { label: 'Health',          emoji: '💊', color: 'bg-red-500/15',     text: 'text-red-400' },
  utilities:     { label: 'Utilities',       emoji: '⚡', color: 'bg-yellow-500/15',  text: 'text-yellow-400' },
  education:     { label: 'Education',       emoji: '📚', color: 'bg-indigo-500/15',  text: 'text-indigo-400' },
  travel:        { label: 'Travel',          emoji: '✈️', color: 'bg-cyan-500/15',    text: 'text-cyan-400' },
  subscriptions: { label: 'Subscriptions',   emoji: '🔄', color: 'bg-violet-500/15',  text: 'text-violet-400' },
  other:         { label: 'Other',           emoji: '📦', color: 'bg-gray-500/15',    text: 'text-gray-400' },
};

const CURRENCIES = ['AED'];
const CATEGORIES = Object.keys(CATEGORY_CONFIG) as ExpenseCategory[];

const ExpenseModal: React.FC<ExpenseModalProps> = ({
  isOpen, onClose, onSaved, editingExpense, defaultDate,
}) => {
  const today = new Date().toISOString().split('T')[0];
  const [amount,   setAmount]   = useState('');
  const [currency, setCurrency] = useState('AED');
  const [category, setCategory] = useState<ExpenseCategory>('food');
  const [merchant, setMerchant] = useState('');
  const [note,     setNote]     = useState('');
  const [date,     setDate]     = useState(defaultDate ?? today);
  const [loading,  setLoading]  = useState(false);

  useEffect(() => {
    if (editingExpense) {
      setAmount(String(editingExpense.amount));
      setCurrency(editingExpense.currency);
      setCategory(editingExpense.category);
      setMerchant(editingExpense.merchant);
      setNote(editingExpense.note ?? '');
      setDate(editingExpense.date);
    } else {
      setAmount(''); setCurrency('AED'); setCategory('food');
      setMerchant(''); setNote(''); setDate(defaultDate ?? today);
    }
  }, [editingExpense, defaultDate, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const num = Number(amount);
    if (!num || num <= 0 || !merchant.trim()) return;
    setLoading(true);
    const data = { amount: num, currency, category, merchant: merchant.trim(), note: note.trim() || undefined, date };
    if (editingExpense) await updateExpense(editingExpense.id, data);
    else await createExpense(data);
    setLoading(false);
    onSaved();
    onClose();
  };

  const inputClass = "w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-gray-500 bg-white/5 border border-white/10 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-colors";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1.5";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="relative w-full max-w-md rounded-2xl shadow-2xl fade-in-up"
        style={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.10)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <h2 className="text-base font-semibold text-white">{editingExpense ? 'Edit Expense' : 'Add Expense'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1 rounded-lg hover:bg-white/10"><X className="h-5 w-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Category picker */}
          <div>
            <label className={labelClass}>Category</label>
            <div className="grid grid-cols-5 gap-1.5">
              {CATEGORIES.map((cat) => {
                const cfg = CATEGORY_CONFIG[cat];
                return (
                  <button key={cat} type="button"
                    onClick={() => setCategory(cat)}
                    title={cfg.label}
                    className={`flex flex-col items-center gap-1 py-2 rounded-xl text-xs transition-all ${
                      category === cat
                        ? `${cfg.color} ${cfg.text} border border-current/30 font-medium`
                        : 'bg-white/5 text-gray-500 hover:bg-white/10 border border-transparent'
                    }`}>
                    <span className="text-base leading-none">{cfg.emoji}</span>
                    <span className="leading-tight text-center" style={{ fontSize: '9px' }}>{cfg.label.split(' ')[0]}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Merchant */}
          <div>
            <label className={labelClass}>Merchant / Description</label>
            <div className="relative">
              <Store className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <input id="expenseMerchant" className={`${inputClass} pl-9`}
                placeholder="e.g. Starbucks, Uber, Amazon…"
                value={merchant} onChange={(e) => setMerchant(e.target.value)} required />
            </div>
          </div>

          {/* Amount + currency */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className={labelClass}>Amount</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <input id="expenseAmount" className={`${inputClass} pl-9`} type="number" step="0.01" min="0.01"
                  placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} required />
              </div>
            </div>
            <div>
              <label className={labelClass}>Currency</label>
              <select className={`${inputClass} appearance-none`} value={currency} onChange={(e) => setCurrency(e.target.value)}>
                {CURRENCIES.map((c) => <option key={c} value={c} className="bg-gray-800">{c}</option>)}
              </select>
            </div>
          </div>

          {/* Date */}
          <div>
            <label className={labelClass}>Date</label>
            <input className={inputClass} type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          </div>

          {/* Note */}
          <div>
            <label className={labelClass}><AlignLeft className="inline h-3 w-3 mr-1" />Note <span className="text-gray-600">(optional)</span></label>
            <textarea className={`${inputClass} resize-none`} rows={2}
              placeholder="Any extra details…" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl text-sm font-medium text-gray-400 bg-white/5 hover:bg-white/10 transition-colors">Cancel</button>
            <button type="submit" disabled={loading}
              className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 transition-colors shadow-lg shadow-emerald-500/20">
              {loading ? 'Saving...' : editingExpense ? 'Update' : 'Add Expense'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpenseModal;
